import React from 'react';
import { Confidence, ImportLineItem } from '../types';
import { formatTotalHT } from '../utils/formatters';
import LineActions from './LineActions';

interface LineItemRowProps {
  line: ImportLineItem;
  isLast: boolean;
  isHovered: boolean;
  isActive: boolean;
  modifiedFields: Set<string>;
  onUpdate: (id: string, field: keyof ImportLineItem, value: string | number | null) => void;
  onDelete: (id: string) => void;
  onMerge: (id: string) => void;
  onSplit: (id: string) => void;
  onHover: (id: string | null) => void;
  onClick: (id: string | null) => void;
  registerRef: (id: string, el: HTMLTableRowElement | null) => void;
  dragHandleProps?: React.HTMLAttributes<HTMLTableCellElement>;
  isSelected?: boolean;
  onToggleSelect?: (id: string, shiftKey: boolean) => void;
  renderExtraCells?: (line: ImportLineItem) => React.ReactNode;
  extraColumnCount?: number;
  hiddenColumns?: Set<string>;
}

const confidenceBg: Record<Confidence, string> = {
  low: 'bg-orange-50',
  medium: 'bg-yellow-50/30',
  high: '',
};

const cellConfidenceBorder: Record<Confidence, string> = {
  low: 'border-l-2 border-l-orange-400',
  medium: 'border-l-2 border-l-yellow-400',
  high: '',
};

const LineItemRow: React.FC<LineItemRowProps> = ({
  line,
  isLast,
  isHovered,
  isActive,
  modifiedFields,
  onUpdate,
  onDelete,
  onMerge,
  onSplit,
  onHover,
  onClick,
  registerRef,
  dragHandleProps,
  isSelected,
  onToggleSelect,
  renderExtraCells,
  hiddenColumns,
}) => {
  const hidden = hiddenColumns ?? new Set<string>();
  const show = (col: string) => !hidden.has(col);
  const hasHidden = hidden.size > 0;

  const handleBlur = (field: keyof ImportLineItem, rawValue: string) => {
    if (field === 'quantite' || field === 'prixUnitaire') {
      const parsed = rawValue === '' ? null : parseFloat(rawValue.replace(',', '.'));
      onUpdate(line.id, field, isNaN(parsed as number) ? null : parsed);
    } else {
      onUpdate(line.id, field, rawValue);
    }
  };

  const isModified = (field: string) => modifiedFields.has(`${line.id}:${field}`);

  const getCellConfidence = (field: string): Confidence => {
    if (hasHidden) return 'high'; // No confidence indicators in classifying mode
    const cell = line.cellConfidence?.[field as keyof typeof line.cellConfidence];
    return cell ?? line.confidence;
  };

  const highlightClass = isActive
    ? 'bg-violet-50'
    : isHovered
    ? 'bg-gray-50'
    : '';

  const inputCls = (base: string, field: string) =>
    `${base} ${isModified(field) ? 'border-b !border-violet-300' : ''}`;

  // Title row colspan: count visible columns after article (designation + visible numeric cols)
  const titleColSpan = ['designation', 'unite', 'quantite', 'prixUnitaire', 'totalHT'].filter(
    (c) => show(c)
  ).length;

  // ── Title row ──
  if (line.isTitle) {
    return (
      <tr
        ref={(el) => registerRef(line.id, el)}
        className={`border-b border-gray-200 bg-gray-50/80 transition-colors ${
          isActive ? '!bg-violet-50' : isHovered ? '!bg-gray-100' : ''
        }`}
        onMouseEnter={() => onHover(line.id)}
        onMouseLeave={() => onHover(null)}
        onClick={() => onClick(line.id)}
      >
        <td className="w-8 text-center cursor-grab text-gray-300" {...dragHandleProps}>
          ⠿
        </td>
        <td
          className="w-8 text-center select-none"
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onToggleSelect?.(line.id, e.shiftKey);
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={isSelected ?? false}
            readOnly
            aria-label={`Sélectionner ${line.designation}`}
            className="w-3.5 h-3.5 rounded border-gray-300 text-violet-600 focus:ring-violet-500 cursor-pointer pointer-events-none"
          />
        </td>
        {show('article') && (
          <td className="w-24 px-1 py-1 text-xs font-bold text-gray-500">
            {line.article}
          </td>
        )}
        <td colSpan={titleColSpan} className="px-1 py-1">
          <input
            type="text"
            defaultValue={line.designation}
            onBlur={(e) => handleBlur('designation', e.target.value)}
            className={inputCls(
              'w-full bg-transparent border-b border-transparent focus:border-violet-400 outline-none text-sm font-semibold text-gray-700 tracking-wide py-0.5 px-1',
              'designation'
            )}
          />
        </td>
        {renderExtraCells?.(line)}
        <td className="w-10">
          <LineActions
            lineId={line.id}
            isMerged={line.isMerged}
            isLast={isLast}
            onDelete={onDelete}
            onMerge={onMerge}
            onSplit={onSplit}
          />
        </td>
      </tr>
    );
  }

  // ── Data row ──
  const rowConfidenceBg = !hasHidden && !isActive && !isHovered ? confidenceBg[line.confidence] : '';

  const cellCls = (field: string, base: string) => {
    const conf = getCellConfidence(field);
    return `${base} ${cellConfidenceBorder[conf]}`;
  };

  return (
    <tr
      ref={(el) => registerRef(line.id, el)}
      className={`border-b border-gray-100 transition-colors ${highlightClass} ${rowConfidenceBg}`}
      onMouseEnter={() => onHover(line.id)}
      onMouseLeave={() => onHover(null)}
      onClick={() => onClick(line.id)}
    >
      {/* Drag handle */}
      <td className="w-8 text-center cursor-grab text-gray-300" {...dragHandleProps}>
        ⠿
      </td>

      {/* Checkbox */}
      <td
        className="w-8 text-center select-none"
        onMouseDown={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onToggleSelect?.(line.id, e.shiftKey);
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          checked={isSelected ?? false}
          readOnly
          aria-label={`Sélectionner ${line.article} ${line.designation}`}
          className="w-3.5 h-3.5 rounded border-gray-300 text-violet-600 focus:ring-violet-500 cursor-pointer pointer-events-none"
        />
      </td>

      {/* Article */}
      {show('article') && (
        <td className={cellCls('article', 'w-24 px-1')}>
          <input
            type="text"
            defaultValue={line.article}
            onBlur={(e) => handleBlur('article', e.target.value)}
            className={inputCls(
              'w-full bg-transparent border-b border-transparent focus:border-violet-400 outline-none text-sm py-1 px-1',
              'article'
            )}
          />
        </td>
      )}

      {/* Désignation */}
      {show('designation') && (
        <td className={cellCls('designation', 'px-1')}>
          <input
            type="text"
            defaultValue={line.designation}
            onBlur={(e) => handleBlur('designation', e.target.value)}
            className={inputCls(
              'w-full bg-transparent border-b border-transparent focus:border-violet-400 outline-none text-sm py-1 px-1',
              'designation'
            )}
          />
        </td>
      )}

      {/* Unité */}
      {show('unite') && (
        <td className={cellCls('unite', 'w-16 px-1')}>
          <input
            type="text"
            defaultValue={line.unite}
            onBlur={(e) => handleBlur('unite', e.target.value)}
            className={inputCls(
              'w-full bg-transparent border-b border-transparent focus:border-violet-400 outline-none text-sm py-1 px-1 text-center',
              'unite'
            )}
          />
        </td>
      )}

      {/* Quantité */}
      {show('quantite') && (
        <td className={cellCls('quantite', 'w-20 px-1')}>
          <input
            type="text"
            defaultValue={line.quantite !== null ? String(line.quantite) : ''}
            onBlur={(e) => handleBlur('quantite', e.target.value)}
            className={inputCls(
              'w-full bg-transparent border-b border-transparent focus:border-violet-400 outline-none text-sm py-1 px-1 text-right',
              'quantite'
            )}
          />
        </td>
      )}

      {/* Prix unitaire */}
      {show('prixUnitaire') && (
        <td className={cellCls('prixUnitaire', 'w-24 px-1')}>
          <input
            type="text"
            defaultValue={line.prixUnitaire !== null ? String(line.prixUnitaire) : ''}
            onBlur={(e) => handleBlur('prixUnitaire', e.target.value)}
            className={inputCls(
              'w-full bg-transparent border-b border-transparent focus:border-violet-400 outline-none text-sm py-1 px-1 text-right',
              'prixUnitaire'
            )}
          />
        </td>
      )}

      {/* Total HT */}
      {show('totalHT') && (
        <td className="w-28 px-2 text-right text-sm font-medium text-gray-700">
          {formatTotalHT(line.quantite, line.prixUnitaire)}
        </td>
      )}

      {/* Extra columns (classifying) */}
      {renderExtraCells?.(line)}

      {/* Actions */}
      <td className="w-10">
        <LineActions
          lineId={line.id}
          isMerged={line.isMerged}
          isLast={isLast}
          onDelete={onDelete}
          onMerge={onMerge}
          onSplit={onSplit}
        />
      </td>
    </tr>
  );
};

export default LineItemRow;
